import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MagnifyingGlass, CircleNotch } from "@phosphor-icons/react";

/**
 * Autocomplete input for stock tickers. Debounced search against
 * ``GET /api/market/search`` — shows a dropdown of matches with company name
 * + logo (Financial Modeling Prep CDN, falls back to initials).
 *
 * Controlled via ``value`` / ``onChange`` on the raw symbol string. Consumers
 * can also pass ``onSelect`` for the click-a-suggestion event.
 */
export default function SymbolSearchInput({
  value,
  onChange,
  onSelect,
  placeholder = "Search AAPL, MSFT…",
  testId = "symbol-search-input",
  className = "",
  autoFocus = false,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Debounce so we don't hit the API on every keystroke
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (value || "").trim();
    if (!q) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/market/search?q=${encodeURIComponent(q.toUpperCase())}`);
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    function onClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (r) => {
    onChange?.(r.symbol);
    onSelect?.(r);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0 && results[active]) { e.preventDefault(); pick(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid={`${testId}-wrap`}>
      <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10" />
      {loading && (
        <CircleNotch size={13} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10" />
      )}
      <input
        type="text"
        autoFocus={autoFocus}
        value={value || ""}
        onChange={(e) => { onChange?.(e.target.value.toUpperCase()); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full h-9 pl-8 pr-8 bg-zinc-950 border border-zinc-700 rounded-sm mono text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors"
      />
      {open && results.length > 0 && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-sm shadow-xl"
          data-testid={`${testId}-dropdown`}
        >
          {results.map((r, i) => (
            <button
              key={r.symbol}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
              onMouseEnter={() => setActive(i)}
              data-testid={`${testId}-option-${r.symbol}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border-b border-zinc-800/60 last:border-b-0 ${
                i === active ? "bg-zinc-800/60" : "hover:bg-zinc-800/40"
              }`}
            >
              <SymbolLogo symbol={r.symbol} logo={r.logo} />
              <div className="flex-1 min-w-0">
                <div className="mono text-sm font-semibold text-white">{r.symbol}</div>
                <div className="text-[10px] text-zinc-500 truncate">{r.name}</div>
              </div>
              {r.price != null && (
                <div className="mono text-xs text-zinc-400">${Number(r.price).toFixed(2)}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Logo with initials fallback when the CDN 404s. */
export function SymbolLogo({ symbol, logo, size = 24 }) {
  const [failed, setFailed] = useState(false);
  const initials = (symbol || "?").slice(0, 2);
  if (failed || !logo) {
    return (
      <div
        className="flex items-center justify-center rounded-sm bg-zinc-800 mono text-[9px] font-bold text-zinc-300 shrink-0"
        style={{ width: size, height: size }}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={logo}
      alt={symbol}
      onError={() => setFailed(true)}
      className="rounded-sm shrink-0 bg-white"
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  );
}
